import { mergeStyleSets } from "@fluentui/react";
import { makeStyles, Spinner } from "@fluentui/react-components";
import {
  AirplaneLandingFilled,
  AirplaneTakeOffFilled,
} from "@fluentui/react-icons";
import React, { useEffect, useState } from "react";
import { AmdDonutChart } from "../../../components/common/charts/DonutChart/DonutChart";
import { AmdAirplane } from "../../../components/common/icons/airplane/airplane";
import { NoDataCard } from "../../../components/common/no-data-card/NoDataCard";
import { LayoutContext } from "../../../context/LayoutContext";
import { $cloud90, $sky60 } from "../../../cssjs/colors";
import { FlightCardTypes } from "../../../enum/flights/FlightEnum";
import { TimeMode } from "../../../enum/flights/FlightTableEnum";
import { useApiCall } from "../../../hooks/useApiCall";
import {
  IFlightSummaryKpi,
  IFlightSummaryResponse,
} from "../../../models/flights/IFlightSummary";
import { CommonHelper } from "../../../utils/common/CommonHelper";
import { EMPTY_STATE_MESSAGE } from "../../../utils/common/Constants";
import {
  buildFlightSummaryPayload,
  getFlightSummaryResult,
} from "../../../utils/flights/FlightSummaryApi";
import { FlightSummaryHelper } from "../../../utils/flights/FlightSummaryHelper";
import AirplaneSvg from "./../../../assests/airplane.png";

const FlightSummaryCard: React.FunctionComponent<any> = ({
  airport,
  selectedSection,
  onSectionChange,
  scheduleTime,
}) => {
  const styles = useStyles();
  const { globalFilterDate } = React.useContext(LayoutContext);
  const { timezone } = React.useContext(LayoutContext);

  const getFlightKpiUrl = `${process.env.REACT_APP_API_FLIGHTSUMMARY?.replace(
    "{airportCode}",
    airport
  )}`;
  const timeMode: TimeMode = scheduleTime ? "Scheduled" : "Best";

  const [flightData, setFlightData] = useState<{
    arrival: IFlightSummaryKpi | undefined;
    overall: IFlightSummaryKpi | undefined;
    departure: IFlightSummaryKpi | undefined;
  }>({
    arrival: undefined,
    overall: undefined,
    departure: undefined,
  });

  const payload = buildFlightSummaryPayload(
    airport,
    globalFilterDate.startDate,
    globalFilterDate.endDate,
    timeMode,
    timezone
  );

  const {
    data: getFlightKpiResponses,
    error,
    isLoading,
  } = useApiCall<IFlightSummaryResponse[]>({
    requestType: "post",
    url: getFlightKpiUrl,
    payload,
    dependencies: [airport, timezone],
    showLoaderOnFirstCallOnly: false,
    showLoaderOnDependencyChangeOnly: true,
  });

  const handleClick = (cardName: string) => {
    if (cardName !== null) {
      onSectionChange(cardName);
    }
  };

  useEffect(() => {
    const newFlightData: {
      arrival: IFlightSummaryKpi | undefined;
      overall: IFlightSummaryKpi | undefined;
      departure: IFlightSummaryKpi | undefined;
    } = { arrival: undefined, overall: undefined, departure: undefined };

    if (getFlightKpiResponses) {
      getFlightSummaryResult(getFlightKpiResponses, airport).forEach((item) => {
        if (item.type === "Overall") {
          newFlightData.overall = item;
        } else if (item.type === "Arrivals") {
          newFlightData.arrival = item;
        } else if (item.type === "Departures") {
          newFlightData.departure = item;
        }
      });
    }

    setFlightData(newFlightData);
  }, [airport, getFlightKpiResponses]);

  const arrivalChartData = FlightSummaryHelper.getDonutChartData(
    flightData.arrival
  );
  const legendData = arrivalChartData.map((data: any) => {
    return {
      color: data.resourceColor,
      legendText: `${data.resourceTitle} ${data.resourceCount?.toString()}`,
      legendValue: data.resourceTitle,
      testId: data.resourceTitle,
    };
  });

  return (
    <div
      className={styles.cardContainer}
      style={{
        border: `2px solid ${
          selectedSection === FlightCardTypes.SUMMARY ? $sky60 : "#ccc"
        }`,
      }}
      onClick={() => handleClick(FlightCardTypes.SUMMARY)}
      data-testid="flight360-summary-card"
    >
      {error && !isLoading && (
        <NoDataCard message={EMPTY_STATE_MESSAGE.Flights} />
      )}
      {isLoading && (
        <Spinner
          labelPosition="below"
          label="Loading..."
          className={`${styles.cardLoader}`}
        />
      )}
      {!error && !isLoading ? (
        <>
          <div className={`${styles.summaryBox}`}>
            <div className={` ${styles.airplaneContainer}`}></div>
            <div
              className={`${styles.summaryAlign} d-flex amd-container-vcolumn`}
            >
              <div className="d-flex">
                <AmdAirplane />
                <span className="fs-22 fw-700">&nbsp;Flights</span>
              </div>
            </div>

            <AmdDonutChart
              resourceData={FlightSummaryHelper.getDonutChartData(
                flightData.overall
              )}
              noFlightsMessage="No Data found"
              hideLegend={true}
              radius={40}
              width={80}
              height={75}
              hideValueInsideDonut={false}
              donutContainerWidth="165px"
              chartAlignment="left"
              showCustomLegend={true}
              wrapperClassName="pt-3"
            />
          </div>
          <div
            className={`amd-container-vcolumn align-items-center ${styles.cardPerformanceFactorItemContainer}`}
          >
            <div
              className={`d-flex fw-500 fs-14 ${styles.legendAlignment} ${styles.fontcolor}`}
            >
              <div className="fs-14 " style={{ minWidth: "90px" }}></div>
              {legendData?.map((legendItem: any) => {
                const colorKey = legendItem.color;
                return (
                  <div
                    key={`${legendItem.legendValue}-${colorKey}`}
                    className={`d-flex align-items-center `}
                  >
                    <span
                      className={getLegendSquareStyle(colorKey).legendSquare}
                    />
                    <span style={{ paddingLeft: "3px" }}>
                      {legendItem.legendValue}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-1">
              {getFlightSummaryResult(getFlightKpiResponses, airport).map(
                (resource) => {
                  return (
                    <div
                      key={CommonHelper.getGuid()}
                      className={`d-flex`}
                      style={{ lineHeight: "2" }}
                    >
                      <div
                        className="d-flex fs-14 align-items-center"
                        style={{ minWidth: "122px" }}
                      >
                        {resource.type}
                        {resource.type !== "Overall" && (
                          <div className={`${styles.fontcolor} ml-1`}>
                            {resource.type === "Arrivals" ? (
                              <AirplaneLandingFilled />
                            ) : (
                              <AirplaneTakeOffFilled />
                            )}
                          </div>
                        )}
                      </div>
                      {resource?.breakdown?.length > 0 && (
                        <>
                          {resource.breakdown.map((item: any) => (
                            <div
                              className={`d-flex flex-1 pl-4 ${styles.legendAlignment}`}
                              key={CommonHelper.getGuid()}
                            >
                              <span className="fs-16">
                                {Math.round(item.kpiValue)}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default FlightSummaryCard;

const getLegendSquareStyle = (bgColor = "") =>
  mergeStyleSets({
    legendSquare: {
      width: 10,
      height: 10,
      display: "inline-block",
      margin: "5px 2px 2px 2px",
      backgroundColor: bgColor,
      borderRadius: "2px",
    },
  });

const useStyles = makeStyles({
  summaryBox: {
    display: "flex",
    height: "157px",
  },
  legendAlignment: {
    justifyContent: "space-between",
  },
  summaryAlign: {
    justifyContent: "center",
  },
  airplaneContainer: {
    flex: 1,
    backgroundSize: "93%",
    backgroundImage: `url(${AirplaneSvg})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "top",
  },
  cardContainer: {
    width: "clamp(400px,50%, max(50%, 400px))",
    height: "330px",
    borderRadius: "8px",
    padding: "20px 20px 0px 20px",
    margin: "10px",
  },
  cardPerformanceFactorItemContainer: {
    color: $cloud90,
    padding: "35px 0px 0px 0px",
  },
  fontcolor: {
    color: $sky60,
  },
  cardLoader: {
    height: "100%",
    minHeight: "173px",
  },
});
